import React from 'react';
import { useDispatch } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleSpinner } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import KycFields from 'apps/auth/components/kyc/KycFields';
import KycGuideScreen from 'apps/auth/components/kyc/KycGuideScreen';
import BasePage from 'apps/auth/components/utils/BasePage';
import ModalWithTimer from 'apps/auth/components/utils/ModalWithTimer';
import { UZBEKISTAN_RESIDENCY_CONTINUE_CLICKED, UZBEKISTAN_RESIDENCY_SCREEN_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { KYC_FIELDS_SCREEN, KYC_GUIDE_SCREEN, KYC_INFO_SCREEN } from 'apps/auth/constants/ComponentNames';
import { KYC_UZBEKISTAN_FORM_NAME } from 'apps/auth/constants/formName';
import { ADD_EXTRA_MY_ID_UZ, AUTH_SEND_MAIN_LINK, MY_ID_KYC_URL_DEV, MY_ID_KYC_URL_PROD } from 'apps/auth/constants/UrlConstants';
import { useExtra, useForm, usePageMessages } from 'apps/auth/hooks/selectors';
import {
    checkValueAndError,
    FAILED,
    getQueryParams,
    INTRO,
    makeMyIdUrl,
    makeRandomString,
    makeUrlWithSearchParams,
    sendingFieldsToServer,
    STOP,
    SUCCESS,
    useMyIdKycSetScreens,
} from 'apps/auth/services/kycService';

const MyIdKycPage = () => {
    const form = useForm(KYC_UZBEKISTAN_FORM_NAME);
    const messages = usePageMessages();
    const extra = useExtra();
    const dispatch = useDispatch();
    const { myIdCode, origin } = getQueryParams();
    const { screen, info, setState } = useMyIdKycSetScreens(myIdCode, extra?.successUzbekistanCheck, extra?.isShowFieldsScreen);
    const myIdKycUrl = __isProd__ ? MY_ID_KYC_URL_PROD : MY_ID_KYC_URL_DEV;
    const href = makeMyIdUrl(makeRandomString, makeUrlWithSearchParams)(myIdKycUrl, extra?.uzClientId, origin);
    const fields = form?.fields ?? {};
    const sendingFieldsToServerCurried = sendingFieldsToServer(toggleSpinner, dispatch);

    const infoScreenMap = {
        [FAILED]: {
            animatedIconFail: true,
            headerText: messages.statusFailed,
            secondaryText: '',
            primaryButtonType: 'button',
            primaryButtonCaption: messages.contactSupport,
            primaryButtonOnClick: () => toggleChat(true)(dispatch),
            primaryButtonLink: '',
            topButtonCaption: '',
            topButtonType: 'button',
            topButtonLink: '',
        },
        [INTRO]: {
            iconName: 'id-card-80',
            iconColor: 'primary',
            headerText: <DangerouslyHtml dangerouslyHtml={messages.screenTitle} />,
            secondaryText: <DangerouslyHtml dangerouslyHtml={messages.screenText} />,
            primaryButtonType: 'button',
            primaryButtonCaption: messages.continueButton,
            primaryButtonOnClick: () =>
                setState((prevState) => {
                    return {
                        ...prevState,
                        screen: KYC_GUIDE_SCREEN,
                    };
                }),
            primaryButtonLink: '',
            topButtonCaption: messages.notNowButton,
            topButtonType: 'link',
            topButtonLink: AUTH_SEND_MAIN_LINK,
        },
        [STOP]: {
            iconName: 'id-card-attention-80',
            iconColor: 'primary',
            headerText: <DangerouslyHtml dangerouslyHtml={messages.noPinflTitle} />,
            secondaryText: <DangerouslyHtml dangerouslyHtml={messages.noPinflText} />,
            primaryButtonType: 'button',
            primaryButtonCaption: messages.contactSupport,
            primaryButtonOnClick: () => toggleChat(true)(dispatch),
            primaryButtonLink: '',
            topButtonCaption: '',
            topButtonType: 'button',
            topButtonLink: '',
        },
    };

    const onClickBackIcon = (e) => {
        if (screen === KYC_GUIDE_SCREEN) {
            e.preventDefault();
            setState({
                screen: KYC_INFO_SCREEN,
                info: INTRO,
            });
        } else if (screen === KYC_INFO_SCREEN && info === STOP) {
            e.preventDefault();
            setState((prevState) => {
                return {
                    ...prevState,
                    screen: KYC_FIELDS_SCREEN,
                };
            });
        }
    };
    const onClickHandler = () => {
        amplitude.getInstance().logEvent(UZBEKISTAN_RESIDENCY_CONTINUE_CLICKED);
        const hasValueAndNoErrors = Object.values(fields).every(checkValueAndError);
        if (hasValueAndNoErrors) {
            sendingFieldsToServerCurried(
                ADD_EXTRA_MY_ID_UZ,
                fields,
                () => {
                    setState({
                        screen: KYC_INFO_SCREEN,
                        info: INTRO,
                    });
                },
                () => {
                    setState({
                        screen: KYC_INFO_SCREEN,
                        info: FAILED,
                    });
                },
            );
        } else {
            setState({
                screen: KYC_INFO_SCREEN,
                info: STOP,
            });
        }
    };

    const componentMap = {
        [KYC_GUIDE_SCREEN]: (
            <KycGuideScreen
                title={messages.suggestionTitle}
                subTitle={messages.suggestionSubtitle}
                caption={messages.readyButton}
                chatTitle={messages.chatLinkTitle}
                items={extra.suggestionItems}
                href={href}
            />
        ),
        [KYC_FIELDS_SCREEN]: (
            <KycFields
                eventName={UZBEKISTAN_RESIDENCY_SCREEN_SHOWN}
                title={messages.kycTitle}
                subtitle={messages.kycSubTitle}
                caption={messages.continueButton}
                inputPassportPlaceholder={messages.pinflText}
                inputPassportComment={messages.pinfl14longText}
                toCountry={extra?.toCountry}
                formName={KYC_UZBEKISTAN_FORM_NAME}
                fields={Object.values(fields)}
                onClickHandler={onClickHandler}
            />
        ),
        [KYC_INFO_SCREEN]: (
            <InfoPage
                iconName={infoScreenMap[info]?.iconName}
                iconColor={infoScreenMap[info]?.iconColor}
                headerText={infoScreenMap[info]?.headerText}
                secondaryText={infoScreenMap[info]?.secondaryText}
                primaryButtonType={infoScreenMap[info]?.primaryButtonType}
                primaryButtonCaption={infoScreenMap[info]?.primaryButtonCaption}
                primaryButtonOnClick={infoScreenMap[info]?.primaryButtonOnClick}
                primaryButtonLink={infoScreenMap[info]?.primaryButtonLink}
                topButtonCaption={infoScreenMap[info]?.topButtonCaption}
                topButtonType={infoScreenMap[info]?.topButtonType}
                topButtonLink={infoScreenMap[info]?.topButtonLink}
            />
        ),
        [SUCCESS]: (
            <ModalWithTimer
                text={messages.statusSuccess}
                onClick={() => {
                    setState({
                        screen: '',
                        info: '',
                    });
                    window.location.href = extra?.linkToDraft || AUTH_SEND_MAIN_LINK;
                }}
            />
        ),
    };
    const backUrl = screen === KYC_FIELDS_SCREEN || screen === KYC_GUIDE_SCREEN || (screen === KYC_INFO_SCREEN && info === STOP) ? AUTH_SEND_MAIN_LINK : '';
    return (
        <CommonPage isTagA backUrl={backUrl} onClickIcon={onClickBackIcon} showChat={false}>
            {screen ? componentMap[screen] : null}
        </CommonPage>
    );
};
export default BasePage(MyIdKycPage);
